// public/v3/src/lib/bhi-suggest.js — BHI eligibility heuristic.
//
// Mirrors v2's psych detection (ccm-compliance.js:3828), but uses the
// structured Elation condition shape too: a patient is BHI-eligible if they
// have a behavioral-health condition by ICD-10 or chart text, OR are on a
// known, high-signal psychotropic medication.
//
// Suggested rating scale is derived from the condition family:
//   F30-F39 (mood)      → PHQ-9 (with MDQ override for F31.* bipolar)
//   F40-F48 (anxiety)   → GAD-7 (with PCL-5 override for F43.10 PTSD)
//   F10           → AUDIT-C
//   F11-F19       → DAST-10
//   F20-F29       → PHQ-9 (proxy)
//
// If the most relevant scale is disabled at the practice level (per
// settings.bhi_enabled_scales), the suggestion falls through to the
// next-most-relevant enabled scale. If no enabled scale exists at all,
// eligible=false (BHI billing requires at least one enabled scale).
//
// Pure functions only. CommonJS for Jest.

const { resolveEffectiveScales } = require('./bhi-scales.js');

const PSYCHOTROPIC_GENERICS = [
  'sertraline', 'fluoxetine', 'escitalopram', 'paroxetine', 'citalopram',
  'venlafaxine', 'duloxetine', 'bupropion', 'mirtazapine', 'trazodone',
  'lithium', 'valproate', 'lamotrigine', 'quetiapine', 'aripiprazole',
  'olanzapine', 'risperidone', 'haloperidol',
  'alprazolam', 'lorazepam', 'clonazepam',
  'buspirone', 'hydroxyzine',
  'methylphenidate', 'amphetamine', 'atomoxetine',
  'naltrexone', 'buprenorphine', 'suboxone', 'varenicline',
];

// Parse an ICD-10 F-prefix from a condition description or structured ICD-10.
// Returns { fNumber, sub, isBipolar (F31.*), isPTSD (F43.10) }.
function parseFCode(desc) {
  const m = String(desc || '').toUpperCase().match(/\bF(\d{2})(?:\.([A-Z0-9]+))?/);
  if (!m) return null;
  const fNumber = parseInt(m[1], 10);
  const sub = m[2] || '';
  return {
    fNumber,
    sub,
    isBipolar: fNumber === 31,
    isPTSD: fNumber === 43 && sub.startsWith('10'),
    isAlcohol: fNumber === 10,
    isTobacco: fNumber === 17,
  };
}

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/['‘’]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function conditionText(c) {
  if (typeof c === 'string') return c.trim();
  if (!c || typeof c !== 'object') return '';
  return String(c.description || c.label || c.name || c.conditionId || '').trim();
}

function conditionCodes(c) {
  if (!c || typeof c !== 'object') return [];
  const out = [];
  const add = (v) => {
    if (Array.isArray(v)) v.forEach(add);
    else if (v != null && String(v).trim()) out.push(String(v).trim());
  };
  add(c.icd10);
  add(c.code);
  return out;
}

function scaleOrderForParsed(parsed) {
  if (parsed?.isPTSD) return ['PCL-5', 'PHQ-9'];
  if (parsed?.isBipolar) return ['MDQ', 'PHQ-9'];
  if (parsed?.isAlcohol) return ['AUDIT-C', 'DAST-10'];
  if (parsed?.isTobacco) return [];
  if (parsed && parsed.fNumber >= 11 && parsed.fNumber <= 19) return ['DAST-10', 'AUDIT-C'];
  if (parsed && parsed.fNumber >= 30 && parsed.fNumber <= 39) return ['PHQ-9', 'GAD-7'];
  if (parsed && parsed.fNumber >= 40 && parsed.fNumber <= 48) return ['GAD-7', 'PHQ-9'];
  if (parsed && parsed.fNumber >= 20 && parsed.fNumber <= 29) return ['PHQ-9'];
  return ['PHQ-9', 'GAD-7'];
}

function rankForParsed(parsed) {
  if (parsed?.isPTSD) return 10;
  if (parsed?.isBipolar) return 20;
  if (parsed && parsed.fNumber >= 30 && parsed.fNumber <= 39) return 30;
  if (parsed && parsed.fNumber >= 40 && parsed.fNumber <= 48) return 40;
  if (parsed && parsed.fNumber >= 20 && parsed.fNumber <= 29) return 50;
  if (parsed && (parsed.isAlcohol || (parsed.fNumber >= 11 && parsed.fNumber <= 19))) return 60;
  if (parsed && parsed.fNumber >= 50 && parsed.fNumber <= 59) return 70;
  if (parsed && parsed.fNumber >= 1 && parsed.fNumber <= 9) return 80;
  return 90;
}

function textHit(desc) {
  const l = norm(desc);
  if (!l) return null;

  const hit = (family, scaleOrder, rank) => ({ family, scaleOrder, rank, desc: String(desc).trim() });
  const tobaccoOnly = /\b(tobacco|nicotine|cigarette smoker|smoker|smoking)\b/.test(l)
    && !/\b(opioid|cannabis|cocaine|stimulant|sedative|benzodiazepine|alcohol|substance|drug)\b/.test(l);
  if (tobaccoOnly) return null;

  if (/\b(ptsd|post[-\s]*traumatic|trauma(?:tic)? stress)\b/.test(l)) return hit('trauma/stressor disorder', ['PCL-5', 'PHQ-9'], 10);
  if (/\b(bipolar|mania|manic|cyclothym)/.test(l)) return hit('bipolar/mood disorder', ['MDQ', 'PHQ-9'], 20);
  if (/\b(depression|depressive|mdd|dysthymi|mood disorder)\b/.test(l)) return hit('depressive/mood disorder', ['PHQ-9', 'GAD-7'], 30);
  if (/\b(anxiety|gad|panic|phobi|agoraphobi|social anxiety|ocd|obsessive[-\s]*compulsive)\b/.test(l)) return hit('anxiety/obsessive-compulsive disorder', ['GAD-7', 'PHQ-9'], 40);
  if (/\b(schizophrenia|schizoaffective|psychosis|psychotic|delusional)\b/.test(l)) return hit('psychotic disorder', ['PHQ-9'], 50);
  if (/\b(alcohol(?: use)? disorder|alcohol abuse|alcohol dependence|alcoholism)\b/.test(l)) return hit('alcohol use disorder', ['AUDIT-C', 'DAST-10'], 60);
  if (/\b(opioid|cannabis|cocaine|stimulant|sedative|benzodiazepine|substance use|drug abuse|drug dependence|use disorder)\b/.test(l)) return hit('substance use disorder', ['DAST-10', 'AUDIT-C'], 60);
  if (/\b(anorexia|bulimia|binge eating|eating disorder)\b/.test(l)) return hit('eating disorder', ['PHQ-9', 'GAD-7'], 70);
  if (/\b(adjustment disorder|grief|bereavement)\b/.test(l)) return hit('adjustment/stressor disorder', ['PHQ-9', 'GAD-7'], 75);
  if (/\b(suicidal ideation|self[-\s]*harm|self injury)\b/.test(l)) return hit('safety concern', ['PHQ-9'], 25);
  if (/\b(dementia|alzheimer|neurocognitive)\b/.test(l) && /\b(behavior|agitation|psychosis|depression|anxiety|mood)\b/.test(l)) {
    return hit('neurocognitive disorder with behavioral symptoms', ['PHQ-9', 'GAD-7'], 80);
  }
  if (/\b(adhd|attention deficit)\b/.test(l)) return hit('attention-deficit disorder', ['PHQ-9', 'GAD-7'], 85);

  return null;
}

function reasonLabel(desc, code) {
  const d = String(desc || '').trim();
  const c = String(code || '').trim();
  if (d && c && !d.toUpperCase().includes(c.toUpperCase())) return `${d} (${c})`;
  return d || c;
}

// Walk the patient's conditions and pick the best behavioral-health signal.
// Priority beats source order so a row with dementia + depression selects the
// depression condition/PHQ-9 rather than the earlier neurocognitive code.
// Every behavioral hit across all of a patient's conditions, rank-sorted
// (lowest rank = highest priority first). Shared by findBehavioralCondition
// (which takes the top hit) and applicableScales (which takes all of them),
// so the F-code/text matching rules live in exactly one place.
function collectBehavioralHits(patient) {
  const list = Array.isArray(patient?.conditions) ? patient.conditions : [];
  const hits = [];
  for (const c of list) {
    const desc = conditionText(c);
    const codes = conditionCodes(c);

    for (const source of [desc, ...codes]) {
      const parsed = parseFCode(source);
      if (!parsed || parsed.isTobacco) continue;
      if (parsed.fNumber >= 1 && parsed.fNumber <= 9 && !/\b(behavior|agitation|psychosis|depression|anxiety|mood)\b/i.test(desc)) continue;
      hits.push({
        parsed,
        desc: reasonLabel(desc, source),
        scaleOrder: scaleOrderForParsed(parsed),
        rank: rankForParsed(parsed),
      });
    }

    const byText = textHit(desc);
    if (byText) hits.push(byText);
  }
  hits.sort((a, b) => a.rank - b.rank);
  return hits;
}

function findBehavioralCondition(patient) {
  return collectBehavioralHits(patient)[0] || null;
}

function findPsychotropicMatch(patient) {
  const meds = patient?.medications;
  if (!meds) return null;
  const flat = Array.isArray(meds)
    ? meds.map((m) => (typeof m === 'string' ? m : (m?.name || m?.genericName || m?.generic_name || m?.drug || m?.description || ''))).join(' ').toLowerCase()
    : String(meds).toLowerCase();
  for (const generic of PSYCHOTROPIC_GENERICS) {
    if (flat.includes(generic)) return generic;
  }
  return null;
}

// Rank of preferred scales for a condition hit. Walks the list in order and
// returns the first scale that's enabled. If none enabled, returns ''.
function preferredScale(hit, enabledIds) {
  const ordered = hit?.scaleOrder?.length
    ? hit.scaleOrder
    : ['PHQ-9', 'GAD-7'];
  return ordered.find((id) => enabledIds.includes(id)) || '';
}

function suggestBhi(patient, settings) {
  const effective = resolveEffectiveScales(settings || {});
  const enabledIds = effective.map((s) => s.id);

  const fHit = findBehavioralCondition(patient);
  const medHit = findPsychotropicMatch(patient);

  if (!fHit && !medHit) {
    return { eligible: false, recommendedCode: '99484', suggestedScale: '', reasons: [] };
  }

  const scale = preferredScale(fHit || null, enabledIds);
  if (!scale) {
    return { eligible: false, recommendedCode: '99484', suggestedScale: '', reasons: ['No enabled rating scale matches the patient\'s condition'] };
  }

  const reasons = [];
  if (fHit) reasons.push(fHit.desc);
  if (medHit) reasons.push(`on ${medHit}`);
  return {
    eligible: true,
    recommendedCode: '99484',
    suggestedScale: scale,
    reasons,
    reason: reasons.join('; '),
    source: fHit ? 'condition' : 'medication',
  };
}

// Ordered, deduped list of ENABLED scales matching ALL of the patient's
// behavioral conditions (not just the top one). Used by the assessment kiosk
// in scale-mode 'all'. Medication-only eligibility (no condition hit) yields
// the default ['PHQ-9'] when enabled. Disabled scales never appear.
// Uses the shared collectBehavioralHits (rank-sorted, all hits) — NOT
// findBehavioralCondition, which returns only the single top hit and would
// collapse 'all' mode to one scale.
function applicableScales(patient, settings) {
  const effective = resolveEffectiveScales(settings || {});
  const enabledIds = effective.map((s) => s.id);

  const hits = collectBehavioralHits(patient);
  if (!hits.length) {
    // Medication-only eligibility falls back to the default scale.
    if (findPsychotropicMatch(patient)) {
      return enabledIds.includes('PHQ-9') ? ['PHQ-9'] : [];
    }
    return [];
  }

  const out = [];
  const seen = new Set();
  for (const h of hits) {
    const scale = preferredScale(h, enabledIds);
    if (scale && !seen.has(scale)) { seen.add(scale); out.push(scale); }
  }
  return out;
}

module.exports = { suggestBhi, applicableScales, PSYCHOTROPIC_GENERICS, parseFCode };
